<template>
  <div>
    <div class="container-fluid">
      <br />
      <p class="py-1">
        Returnable parts must have been invoiced within the past {{ daysAllowedForExpressReturn }} days to be eligible
        for Express Return.
      </p>
      <div class="modify">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-2">
              <label>
                Search
                <br />For Parts:
              </label>
            </div>
            <div class="col-sm-3">
              <DnInput
                id="txtOemPartCode"
                v-model="enteredPartCode"
                css-class="first form-control addPartInput"
                type="text"
                name="qo-partnum"
                placeholder="Enter Part #"
                tabindex="3"
                maxlength="25"
                :blur-function="GetInvoices"
                :loading="invoicesLoading"
                @input="function () {enteredPartCode = enteredPartCode.toUpperCase().trim();}"
              />
            </div>
            <div class="col-sm-4">
              <b-form-select
                id="invoiceDdl"
                v-model="returnDraftItemDto"
                class="first form-control"
                name="qo-qty"
                tabindex="4"
                :options="invoiceOptions"
                disabled
                @change="invoiceSelected"
              >
                <template slot="first">
                  <option :value="null" disabled>Select Invoice</option>
                </template>
              </b-form-select>
            </div>
            <div class="col-sm-3">
              <DnFunctionButton
                button-id="addDraftToListBtn"
                :callback-fn="AddExpressItem"
                text="Add to Return"
                extra-classes="addPartToListBtn"
                :size="largeSize"
                :loading="draftLoading"
              />
            </div>
          </div>
        </div>
      </div>
      <br />
      <div class="container-fluid">
        <div class="row">
          <DnTable
            id="partsToReturnTbl"
            hover
            striped
            :items="returnDraftDto.Items"
            :fields="partsToReturnColumns"
            stacked="md"
            :no-provider-filtering="true"
            show-empty
            empty-text="No items have been added to this return"
            :no-local-sorting="true"
            class="blue mb-0"
          >
            <template #Quantity="{ row }">
              <b-form-select
                v-model="row.item.Quantity"
                v-validate="{ required: true, is_not: 0 }"
                class="returnQuantity"
                :options="PartQtySetup(row.item.MaxReturnQuantity)"
                :name="'ReturnQuantity_' + row.item.Id"
                aria-describedby="returnQtyLiveFeedback"
                :state="ValidateState('ReturnQuantity_' + row.item.Id)"
                @change="UpdateItem(row.item)"
              >
                <template #first>
                  <option :value="0" disabled>Select</option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback id="claimQtyLiveFeedback">
                Return Quantity is Required.
              </b-form-invalid-feedback>
              <b-form-valid-feedback />
            </template>
            <template #PartDescription="{ row }">
              <div>{{ row.item.PartDescription }}</div>
              <div v-for="(error, key) in row.item.Errors" :key="key" class="alert alert-danger">
                <div v-if="error !== 'LineItemMinimumAmount'">
                  {{ getContentItem(error) }}
                </div>
                <div v-if="error === 'LineItemMinimumAmount'">
                  {{ GetLineItemMinimumAmountErrorMessage(error) }}
                </div>
              </div>
            </template>
            <template #Price="{ row }">
              <CurrencyText :currency-code="row.item.Currency" :amount="row.item.Price" />
            </template>
            <template #Total="{ row }">
              <CurrencyText :currency-code="row.item.Currency" :amount="row.item.Total" />
            </template>
            <template #DeleteRow="{ row }">
              <button
                :id="'btnDeleteRow_' + row.item.Id"
                type="button"
                class="btn btn-small"
                @click="DeleteFromReturn($event, row.item.Id, row.index)"
              >
                <i class="icon-trash" />
              </button>
            </template>
          </DnTable>
          <div id="part-return__total" class="row container no-gutters modify px-2 mb-5">
            <div class="col px-1">
              <strong>TOTAL</strong>
              <strong>
                <CurrencyText :amount="total" class="pull-right" />
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sizeHelper } from "@/components/mixins/sizeHelper";
import DnFunctionButton from "@/components/Elements/DnFunctionButton";
import DnInput from "@/components/Elements/DnInput";
import CurrencyText from "@/components/Elements/CurrencyText";
import { inputValidateState } from "@/components/mixins/inputValidateState";
import { ReturnsService } from "@/services/ReturnsService";
import { formatDateTime } from "@/components/mixins/formatDateTime";
import { returnAddItems } from "./mixins/AddItems";
import { mapGetters } from "vuex";
import { stringHelper } from "@/components/mixins/stringHelper";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  components: {
    DnFunctionButton,
    CurrencyText,
    DnInput,
    DnTable,
  },

  mixins: [sizeHelper, inputValidateState, formatDateTime, returnAddItems, stringHelper],

  props: {
    returnDraftDto: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    invoiceOptions: [],
    enteredPartCode: "",
    returnDraftDtoErrors: { hasErrors: Boolean, errorsList: Array },
    invoicesLoading: false,
    returnDraftItemDto: null,
    total: 0,
    daysAllowedForExpressReturn: 0,
  }),

  computed: {
    ...mapGetters(["getContentItem", "hasPermission"]),
  },
  // I think if we watch the errros on the DTO we should be able to use this watch to drive any errors
  // happening when we try to add the part. It comes back with an Enum numerical value that we'll need
  // to translate.
  // watch: {
  //    "returnDraftDto.Errors": function( val )
  // },
  mounted() {
    this.calculateTotals();
    this.GetAllowedDaysForExpressReturn();
  },

  methods: {
    PartQtySetup(invoiceQuantity) {
      // loop through each part's ordered quantity and set up the selection box
      // to have all quantities between 1 and the total ordered.
      let quantities = [];

      for (let i = 1; i <= invoiceQuantity; i++) {
        quantities.push({ value: i, text: i.toString() });
      }

      return quantities;
    },
    GetLineItemMinimumAmountErrorMessage: function (error) {
      let amount = this.returnDraftDto.MinimumItemAmount;
      let currencyAmount = this.formatToCurrency(amount, this.returnDraftDto.Items[0].Currency);
      return this.format(this.getContentItem(error), currencyAmount);
    },
    AddExpressItem() {
      this.AddDraftItem(true);
    },
    GetInvoices() {
      if (this.enteredPartCode.length === 0) {
        // skip empty input
        return;
      }
      this.returnDraftItemDto = null;
      this.invoiceOptions = [];
      this.invoicesLoading = true;
      let self = this;
      ReturnsService.GetInvoicesForPart(
        this.activeAccountId,
        this.user.UserId,
        this.enteredPartCode,
        true,
        this.hasPermission("IgnoreReturnValidation")
      )
        .then((result) => {
          if (result.data.Invoices.length === 0) {
            var days = result.data.DaysAllowedForExpressReturn;
            this.$store.commit("pushTimedMessage", {
              header: `No invoices found in the last ${days} days.`,
              IsError: true,
              seconds: 20,
            });
            self.invoicesLoading = false;
            return;
          }
          for (let i = 0; i < result.data.Invoices.length; i++) {
            self.invoiceOptions.push({
              value: {
                OemPartCode: result.data.OemPartCode,
                InvoiceId: result.data.Invoices[i].InvoiceId,
                InvoiceSource: result.data.Invoices[i].InvoiceSource,
                InvoiceSourceCode: result.data.Invoices[i].InvoiceSourceCode,
                Quantity: 1,
              },
              text:
                result.data.Invoices[i].InvoiceNumber +
                " | " +
                self.formatDateTime(result.data.Invoices[i].InvoiceDate),
            });
          }
          self.invoicesLoading = false;
          let invoiceDdl = document.getElementById("invoiceDdl");
          invoiceDdl.removeAttribute("disabled");
        })
        .catch(() => {
          self.invoicesLoading = false;
        });
    },
    UpdateItem(item) {
      ReturnsService.UpdateDraftItem(this.activeAccountId, this.user.UserId, this.returnDraftDto.Id, item)
        .then((result) => {
          // update the item in the list
          this.returnDraftDto.Items = result.data.Items;
          this.calculateTotals();
          this.processDraftErrors(result.data);
          this.updateDto(result.data);
        })
        .catch(() => {
          this.$store.commit("pushTimedMessage", {
            header: "Error Updating Item",
            IsError: true,
            seconds: 15,
          });
        });
    },
    invoiceSelected() {
      if (this.returnDraftItemDto.InvoiceSourceCode === "ISC") {
        this.$store.commit("pushTimedMessage", {
          header: "Can’t Create Return From the Selected Invoice",
          message: this.getContentItem("IscalaInvoice"),
          IsError: true,
          seconds: 15,
        });
      }
    },
    GetAllowedDaysForExpressReturn() {
      ReturnsService.GetAllowedDaysForExpressReturn().then((result) => {
        this.daysAllowedForExpressReturn = result.data;
      });
    },
  },
};
</script>

<style>
.returnQuantity {
  padding: 0px !important;
}

#part-return__total {
  max-width: 100%;
}
</style>